import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, User, AlertCircle, Ticket, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { useCoreController } from "@/services/coreController";
import { apiService, appApi } from "@/services/api";
import { analyticsService } from "@/services/analytics";
import { storage } from "@/lib/storage";
import { Notification } from "@/components/Notification";
import { Compte } from "@/types/app";

interface RechargeCouponForm {
  user_id: string;
  compte_id: string;
  coupon_code: string;
}

export function RechargeCouponPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const coreController = useCoreController();

  const [selectedAccount, setSelectedAccount] = useState<Compte | null>(null);
  const [providers, setProviders] = useState<Array<{ id: string; type: string }>>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState<{
    isVisible: boolean;
    message: string;
    type: "error" | "success";
  }>({
    isVisible: false,
    message: "",
    type: "error",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<RechargeCouponForm>();

  const couponCode = watch("coupon_code");

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    analyticsService.trackScreenView("recharge_coupon_page");

    const loadProviders = () => {
      const comptes = coreController.appSettingResponse.compte || [];
      setProviders(comptes);
      if (comptes.length > 0) {
        setSelectedAccount(comptes[0]);
        const savedAccountId = storage.read<string>(`user_id_${comptes[0].type}`);
        if (savedAccountId) {
          setValue("user_id", savedAccountId);
        }
      }
      setIsLoadingProviders(false);
    };

    if (coreController.appSettingResponse.compte) {
      loadProviders();
    } else {
      const checkInterval = setInterval(() => {
        if (coreController.appSettingResponse.compte) {
          loadProviders();
          clearInterval(checkInterval);
        }
      }, 500);
      setTimeout(() => {
        clearInterval(checkInterval);
        setIsLoadingProviders(false);
      }, 10000);
      return () => clearInterval(checkInterval);
    }

    const state = location.state as { prefilledCode?: string } | null;
    if (state?.prefilledCode) {
      setValue("coupon_code", state.prefilledCode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coreController, location]);

  const getThemeColor = (): string => {
    if (!selectedAccount) return "#0052cc";
    switch (selectedAccount.type) {
      case "1XBET":
        return "#002366";
      case "BETWINNER":
        return "#0052cc";
      case "MELBET":
        return "#003d99";
      default:
        return "#0052cc";
    }
  };

  const themeColor = getThemeColor();

  const showNotification = (
    message: string,
    type: "error" | "success" = "error"
  ) => {
    setNotification({ isVisible: true, message, type });
  };

  const hideNotification = () => {
    setNotification((prev) => ({ ...prev, isVisible: false }));
  };

  const handleProviderSelect = (provider: Compte) => {
    setSelectedAccount(provider);
    const savedAccountId = storage.read<string>(`user_id_${provider.type}`);
    if (savedAccountId) {
      setValue("user_id", savedAccountId);
    }
  };

  const onSubmit = async (data: RechargeCouponForm) => {
    if (!selectedAccount) {
      showNotification("Veuillez sélectionner une plateforme");
      return;
    }
    if (isLoading) return;

    setIsLoading(true);

    try {
      const userId = data.user_id;
      const couponCode = data.coupon_code.trim().toUpperCase();

      storage.write(`user_id_${selectedAccount.type}`, userId);

      const checkResult = await apiService.checkRechargeCoupon(couponCode);

      if (!checkResult.valid || !checkResult.amount) {
        showNotification(checkResult.error || "Code de recharge invalide");
        analyticsService.trackError(
          new Error("Invalid recharge coupon code"),
          "recharge_coupon_check"
        );
        return;
      }

      const userName = await appApi.getUserName(userId, selectedAccount.id);

      if (userName === null) {
        showNotification(
          `Votre ID ${selectedAccount.type} semble invalide. Veuillez vérifier l'identifiant ${selectedAccount.type} saisi.`
        );
        analyticsService.trackError(
          new Error("Invalid account ID"),
          "recharge_coupon_user_verification"
        );
        return;
      }
      if (userName.trim() === "") {
        showNotification(
          `Avertissement : Aucun nom associé à votre ID ${selectedAccount.type}.`
        );
      }

      const validateResult = await apiService.validateRechargeCoupon(
        couponCode,
        userId,
        selectedAccount.id
      );

      if (validateResult.success && validateResult.amount) {
        analyticsService.trackEvent("coupon_used", {
          amount: validateResult.amount,
          accountType: selectedAccount.type,
        });

        showNotification(
          `Recharge de ${validateResult.amount.toLocaleString()} FCFA effectuée avec succès !`,
          "success"
        );
        setTimeout(() => navigate("/"), 2000);
      } else {
        showNotification(
          validateResult.error || "Erreur lors de la validation"
        );
      }
    } catch (error: any) {
      showNotification("Erreur lors du traitement du code de recharge");
      analyticsService.trackError(error, "recharge_coupon_page");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingProviders) {
    return (
      <div className="relative flex min-h-[100dvh] flex-col items-center justify-center bg-background text-foreground [padding-top:env(safe-area-inset-top)]">
        <div
          aria-hidden
          className="pointer-events-none fixed inset-0 -z-10"
          style={{ background: "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)" }}
        />
        <Loader2 className="size-8 animate-spin text-text-subtle" />
        <p className="mt-3 text-sm text-muted-foreground">Chargement…</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="relative flex min-h-[100dvh] flex-col bg-background text-foreground [padding-top:env(safe-area-inset-top)] [padding-bottom:env(safe-area-inset-bottom)]"
    >
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{ background: "radial-gradient(120% 80% at 50% 0%, var(--page-gradient-via) 0%, var(--page-gradient-from) 45%, var(--page-gradient-to) 100%)" }}
      />

      {/* App Bar */}
      <header className="sticky top-0 z-30 border-b border-border bg-header-bg backdrop-blur-xl">
        <div className="mx-auto flex max-w-[52ch] items-center gap-3 px-4 py-3 lg:max-w-6xl lg:px-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Retour"
            className="grid size-9 place-items-center rounded-full bg-muted ring-1 ring-card-muted-ring"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold tracking-tight">Coupons de recharge</h1>
            <p className="truncate text-[10px] uppercase tracking-widest" style={{ color: `${themeColor}cc` }}>
              Redeem · utilise un code de recharge
            </p>
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="mx-auto w-full max-w-[52ch] flex-1 px-4 py-6 lg:max-w-6xl lg:px-8">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Platform Selection */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl bg-card p-4 ring-1 ring-border"
          >
            <label htmlFor="provider-select" className="mb-3 block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Sélectionnez la plateforme
            </label>
            <div id="provider-select" className="grid grid-cols-3 gap-1.5 rounded-2xl bg-surface p-1 ring-1 ring-border" role="group" aria-label="Sélectionnez la plateforme">
              {providers.map((provider) => {
                const isSelected = selectedAccount?.type === provider.type;
                const accent =
                  provider.type === "1XBET" ? "var(--accent-brand)" :
                  provider.type === "MELBET" ? "#0052cc" :
                  provider.type === "BETWINNER" ? "#0052cc" : themeColor;
                return (
                  <button
                    key={provider.id}
                    type="button"
                    onClick={() => handleProviderSelect(provider)}
                    style={isSelected ? { background: accent, color: "#fff", boxShadow: `0 6px 20px -8px ${accent}` } : undefined}
                    className={`rounded-xl py-2 text-[13px] font-black tracking-wider transition ${isSelected ? "" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
                  >
                    {provider.type === "1XBET" && (
                      <>
                        <span className={isSelected ? "text-white" : "text-muted-foreground"}>1X</span>
                        <span style={{ color: isSelected ? "#fff" : accent }}>BET</span>
                      </>
                    )}
                    {provider.type === "MELBET" && (
                      <>
                        <span className={isSelected ? "text-white" : "text-muted-foreground"}>MEL</span>
                        <span style={{ color: isSelected ? "#fff" : accent }}>BET</span>
                      </>
                    )}
                    {provider.type === "BETWINNER" && (
                      <>
                        <span className={isSelected ? "text-white" : "text-muted-foreground"}>BET</span>
                        <span style={{ color: isSelected ? "#fff" : accent }}>WINNER</span>
                      </>
                    )}
                    {provider.type && !["1XBET", "MELBET", "BETWINNER"].includes(provider.type) && provider.type}
                  </button>
                );
              })}
            </div>
          </motion.div>

          {/* Account ID Field */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-2 rounded-2xl bg-card p-4 ring-1 ring-border"
          >
            <label htmlFor="user_id" className="block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Information du compte
            </label>
            <div className="relative">
              <div
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-lg p-2"
                style={{ backgroundColor: `${themeColor}1A` }}
              >
                <User className="size-4" style={{ color: themeColor }} />
              </div>
              <input
                id="user_id"
                {...register("user_id", {
                  required: "Ce champ est requis",
                  minLength: { value: 3, message: "L'ID du compte doit comporter au moins 3 caractères" },
                  pattern: { value: /^[0-9]+$/, message: "L'ID du compte doit contenir que des chiffres" },
                })}
                type="text"
                inputMode="numeric"
                placeholder="Entrez votre ID de compte"
                className={`h-auto w-full rounded-xl bg-surface py-3 pl-12 pr-4 text-base text-foreground ring-1 ring-border outline-none transition focus:ring-foreground/20 ${errors.user_id ? "ring-destructive" : ""}`}
              />
            </div>
            {errors.user_id && (
              <p className="flex items-center gap-1 text-sm text-destructive">
                <AlertCircle className="size-4" />
                {errors.user_id.message}
              </p>
            )}
          </motion.div>

          {/* Coupon Code Field */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-2 rounded-2xl bg-card p-4 ring-1 ring-border"
          >
            <label htmlFor="coupon_code" className="block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
              Code de recharge
            </label>
            <div className="relative">
              <div
                className="absolute left-3 top-1/2 -translate-y-1/2 rounded-lg p-2"
                style={{ backgroundColor: `${themeColor}1A` }}
              >
                <Ticket className="size-4" style={{ color: themeColor }} />
              </div>
              <input
                id="coupon_code"
                {...register("coupon_code", { required: "Le code de recharge est requis" })}
                type="text"
                value={couponCode || ""}
                placeholder="RCHG-XXXX-XXXX"
                className={`h-auto w-full rounded-xl bg-surface py-3 pl-12 pr-4 font-mono text-base text-foreground ring-1 ring-border outline-none transition focus:ring-foreground/20 ${errors.coupon_code ? "ring-destructive" : ""}`}
                onChange={(e) => { setValue("coupon_code", e.target.value.toUpperCase()); }}
              />
            </div>
            {errors.coupon_code && (
              <p className="flex items-center gap-1 text-sm text-destructive">
                <AlertCircle className="size-4" />
                {errors.coupon_code.message}
              </p>
            )}
          </motion.div>

          {/* Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="rounded-2xl bg-surface p-4 ring-1 ring-border"
          >
            <div className="flex gap-3">
              <div
                className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-full"
                style={{ backgroundColor: `${themeColor}1A`, color: themeColor }}
              >
                <AlertCircle className="size-4" />
              </div>
              <div>
                <h3 className="mb-1 text-sm font-semibold text-foreground">Information importante</h3>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  En cas d'erreur sur le numéro de compte {selectedAccount?.type}, aucun remboursement ne pourra être effectué. Veuillez vérifier attentivement votre ID de compte avant de valider le code de recharge.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Continue Button */}
          <motion.button
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            type="submit"
            disabled={isLoading}
            className="flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:opacity-90 enabled:active:scale-[0.99]"
            style={{ background: themeColor, color: "#fff" }}
          >
            {isLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <>
                <span>Valider le code</span>
                <ArrowRight className="size-4" />
              </>
            )}
          </motion.button>
        </form>
      </main>

      {/* Notification */}
      <Notification
        message={notification.message}
        type={notification.type}
        isVisible={notification.isVisible}
        onClose={hideNotification}
      />
    </motion.div>
  );
}

export default RechargeCouponPage;
